import React, { useState } from "react";
import { isMobile } from "react-device-detect";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Container, useMediaQuery } from "@mui/material";

import { SxProps, Theme } from "@mui/material/styles";

import Button from "components/Button";
import VideoModal from "components/VideoModal";
import PageNetBackground from "components/PageNetBackground";

import { routes } from "routes/routes";
import { getYoutubeEmbedUrl } from "shared/functions";

import { ReactComponent as PlayIcon } from "assets/images/play-icon.svg";
import { ReactComponent as ListArrowIcon } from "assets/images/icon_list_arrow.svg";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

export interface AdvantageItem {
  title?: string;
  description: string;
}

export interface VideoSectionContent {
  name: string;
  title: string;
  advantages: AdvantageItem[];
  videoCover: string;
  videoUrl: string;
  videoUrlMobile?: string;
}

interface VideoSectionProps {
  section: VideoSectionContent;
  titleSx?: SxProps<Theme>;
}

const VideoSection: React.FC<VideoSectionProps> = (props) => {
  const { section, titleSx } = props;

  const colapseBtns = useMediaQuery(
    "(min-width:900px) and (max-width:1100px), (max-width:600px)",
  );

  const currentVideoId =
    isMobile && section.videoUrlMobile
      ? section.videoUrlMobile
      : section.videoUrl;

  const videoUrl = getYoutubeEmbedUrl(currentVideoId);

  const [showPlayer, setShowPlayer] = useState(false);

  const onGoToRegister = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_oplata_try_for_free,
      { route: window.location.pathname },
      true,
    );

    window.location.href = routes.auth;
  };

  const onClickPlay = () => {
    setShowPlayer(true);
  };

  const onHowItWorksPlay = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_oplata_how_it_works_video_play,
      null,
      true,
    );
    onClickPlay();
  };

  const onVideoPlay = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_oplata_video_play,
      null,
      true,
    );
    onClickPlay();
  };

  const onClosePlayer = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_oplata_video_close,
      null,
      true,
    );
    setShowPlayer(false);
  };

  return (
    <>
      <Box sx={styles.wrapper}>
        <PageNetBackground sx={styles.pageNetBg}>
          <Container>
            <Stack
              direction={{ xs: "column", md: "row" }}
              pt={{ xs: 3, sm: 4, md: 6, lg: 12 }}
              pb={{ xs: 3, sm: 4, md: 6, lg: 12 }}
              gap={3}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{ mb: 1.5 }}
                >
                  {section.name}
                </Typography>
                <Typography
                  variant="h1"
                  color="primary"
                  sx={{ mb: 2.5, width: { xs: "100%", lg: "80%" }, ...titleSx }}
                >
                  {section.title}
                </Typography>
                {section.advantages.map((advantage, index) => (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    key={index}
                    sx={{ mb: 1 }}
                  >
                    <ListArrowIcon
                      style={{ flexShrink: 0, marginTop: "2px" }}
                    />
                    <Typography sx={{ ml: 1.5, fontWeight: 400 }}>
                      {advantage.title && (
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {advantage.title}{" "}
                        </Box>
                      )}

                      {advantage.description}
                    </Typography>
                  </Stack>
                ))}
                <Stack
                  direction={colapseBtns ? "column" : "row"}
                  mt={{ xs: 3, lg: 5 }}
                  gap={2}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onGoToRegister}
                  >
                    Спробувати безкоштовно
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    withBorder
                    onClick={onHowItWorksPlay}
                  >
                    Як це працює?
                  </Button>
                </Stack>
              </Stack>
              <Box sx={styles.thumbnailContainer} onClick={onVideoPlay}>
                <Box
                  component="img"
                  alt="service-img"
                  src={section.videoCover}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <PlayIcon style={styles.playIcon} />
              </Box>
            </Stack>
          </Container>
        </PageNetBackground>
      </Box>
      <VideoModal
        isModalOpen={showPlayer}
        onClose={onClosePlayer}
        videoUrl={videoUrl}
      />
    </>
  );
};

const styles = {
  pageNetBg: {
    borderRadius: { xs: "0px", md: "24px" },
  },
  wrapper: {
    marginLeft: { xs: "0", md: "30px" },
    marginRight: { xs: "0", md: "30px" },
  },
  thumbnailContainer: {
    position: "relative",
    cursor: "pointer",
    lineHeight: 0,
    borderRadius: "20px",
    overflow: "hidden",
    width: { xs: "90%", md: "50%" },
    flexShrink: 0,
    border: "8px solid #E1E8FA",
  },
  playIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
  } as React.CSSProperties,
};

export default VideoSection;
